// src/pages/Exam/ReviewDrawer.jsx
import React, { useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { useExam } from "../../contexts/ExamContext";
import GeneralUtils from "../../utils/GeneralUtils";
import QuestionCard from "./QuestionCard";

const ReviewDrawer = ({ visible, onHide, appendTo }) => {
    const {
        exam,
        questions = [],
        studentAnswers = [],
        answers = {},
        reviewMarks = [],
        currentQuestionIndex,
        setCurrentQuestionIndex,
        persistOpenAnswer,
    } = useExam();

    // use studentAnswers (already ordered by objectIndex) for navigation/numbering
    const ordered = studentAnswers || [];

    const meta = useMemo(() => {
        const byQId = new Map(ordered.map((sa, i) => [sa?.question?.id, { sa, idx: i }]));
        return { byQId };
    }, [ordered]);

    const [filter, setFilter] = useState("all"); // all | answered | marked | unanswered
    const [hoveredId, setHoveredId] = useState(null);

    const isInFilter = (q) => {
        const answered = !!answers[q.id];
        const marked = !!reviewMarks[q.id];
        switch (filter) {
            case "answered":
                return answered;
            case "marked":
                return marked;
            case "unanswered":
                return !answered;
            default:
                return true;
        }
    };

    const getBadgeClass = (q, idx) => {
        const answered = !!answers[q.id];
        const marked = !!reviewMarks[q.id];
        const active = idx === currentQuestionIndex;
        if (active) return "bg-blue-100 border-blue-400 text-blue-900";
        if (marked) return "bg-yellow-100 border-yellow-400 text-yellow-900";
        if (answered) return "bg-green-100 border-green-400 text-green-900";
        return "bg-gray-100 border-gray-300 text-900";
    };

    const hovered = hoveredId ? meta.byQId.get(hoveredId)?.sa?.question : null;

    return (
        <Sidebar
            position="left"
            visible={visible}
            onHide={() => {
                setHoveredId(null);
                onHide && onHide();
            }}
            showCloseIcon
            dismissable
            modal
            style={{ width: "100vw" }}
            header="Gözden Geçir"
            appendTo={appendTo}
        >
            <div className="flex gap-3" style={{ height: "calc(100% - 2rem)" }}>
                <div className="flex flex-column" style={{ width: "50vw" }}>
                    <div className="flex flex-row gap-2 mb-3">
                        <Button size="small" label="Tüm Sorular" outlined={filter !== "all"} onClick={() => setFilter("all")} icon="pi pi-th-large" />
                        <Button size="small" label="Cevapladıklarım" outlined={filter !== "answered"} onClick={() => setFilter("answered")} icon="pi pi-circle-fill" />
                        <Button size="small" label="İşaretlediklerim" outlined={filter !== "marked"} onClick={() => setFilter("marked")} icon={<i className="pi pi-bookmark-fill mr-2" style={{ color: "orange" }}></i>} />
                        <Button size="small" label="Boş Bıraktıklarım" outlined={filter !== "unanswered"} onClick={() => setFilter("unanswered")} icon="pi pi-circle" />
                    </div>

                    <div className="flex-1 overflow-auto pr-2">
                        <div className="grid" style={{ gap: "0.5rem" }}>
                            {ordered.map((sa, idx) => {
                                const q = sa?.question;
                                if (!q) return null;
                                const dim = !isInFilter(q);
                                const num = sa?.objectIndex ?? (idx + 1);
                                const answered = !!answers[q.id];
                                const marked = !!reviewMarks[q.id];

                                return (
                                    <div key={q.id}>
                                        <button
                                            type="button"
                                            aria-label={`Soru ${num}`}
                                            className={`relative p-2 m-2 border-1 border-round w-4rem h-3rem text-center cursor-pointer flex align-items-center justify-content-center select-none transition-all ${getBadgeClass(q, idx)} ${dim ? "opacity-50" : ""} hover:shadow-2 hover:border-blue-400`}
                                            onClick={async () => {
                                                const currentSA = ordered?.[currentQuestionIndex];
                                                const currentQ = currentSA?.question;
                                                if (currentQ?.id) { try { await persistOpenAnswer?.(currentQ.id); } catch {} }
                                                setCurrentQuestionIndex(idx);
                                                onHide && onHide();
                                            }}
                                            onMouseEnter={() => setHoveredId(q.id)}
                                            onFocus={() => setHoveredId(q.id)}
                                            onMouseLeave={() => setHoveredId((id) => (id === q.id ? null : id))}
                                        >
                                            <span className="font-semibold">{num}</span>

                                            {marked && (
                                                <i
                                                    className="pi pi-bookmark-fill"
                                                    style={{
                                                        position: "absolute",
                                                        top: "0.25rem",
                                                        right: "0.25rem",
                                                        fontSize: "0.85rem",
                                                        color: "#f59e0b"
                                                    }}
                                                />
                                            )}

                                            <i
                                                className={`pi ${answered ? "pi-circle-fill" : "pi-circle"}`}
                                                style={{
                                                    position: "absolute",
                                                    right: "0.25rem",
                                                    bottom: "0.25rem",
                                                    fontSize: "0.8rem",
                                                    color: answered ? "#16a34a" : "#6b7280"
                                                }}
                                            />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-auto border-1 surface-border border-round p-3">
                    {hovered ? (
                        <QuestionCard questionOverride={hovered} interactive={false} />
                    ) : (
                        <div className="text-700">Önizleme için bir sorunun üzerine gelin.</div>
                    )}
                </div>
            </div>
        </Sidebar>
    );
};

export default ReviewDrawer;

